import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, User, LogOut, LogIn, Sun, Moon, Phone, Facebook, Instagram, ChevronLeft } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import BrandLogo from '@/components/BrandLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSettings } from '@/contexts/SettingsContext';
import { cn } from '@/lib/utils';

const WhatsAppIcon = (props) => (
  <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
  </svg>
);

const rowClass = ({ isActive } = {}) =>
  cn(
    'group flex items-center gap-3 rounded-xl px-3 py-3 font-cairo font-semibold transition-colors',
    isActive
      ? 'bg-burgundy-500/10 text-burgundy-500'
      : 'text-brand-black hover:bg-gray-100 dark:hover:bg-white/5'
  );

const RowIcon = ({ icon: Icon, active }) => (
  <span
    className={cn(
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
      active ? 'bg-burgundy-500 text-white' : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'
    )}
  >
    <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
  </span>
);

/** قائمة الموبايل: زر همبرغر بيفتح درج جانبي فيه الروابط والحساب والإعدادات. */
const MobileMenu = ({ links }) => {
  const [open, setOpen] = useState(false);
  const site = useSettings();
  const { user, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // أي تنقّل بيسكّر القائمة
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const userName = user?.full_name || user?.name || 'حسابي';

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/');
  };

  const hasContact = site.whatsapp_display || site.facebook || site.instagram || site.whatsapp_url;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="فتح القائمة"
        aria-expanded={open}
        data-testid="mobile-menu-btn"
        className="md:hidden p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      >
        <Menu className="w-6 h-6 text-brand-black" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          dir="rtl"
          className="flex w-[85vw] max-w-sm flex-col gap-0 p-0 [&>button]:hidden"
        >
          <SheetTitle className="sr-only">القائمة</SheetTitle>
          <SheetDescription className="sr-only">روابط الموقع والحساب</SheetDescription>

          {/* الرأس */}
          <div className="flex items-center justify-between border-b border-gray-200/70 dark:border-white/10 px-5 h-20">
            <Link to="/" className="flex items-center">
              <BrandLogo imgClassName="h-12 w-auto" textClassName="text-2xl" />
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="إغلاق القائمة"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
            {/* الحساب */}
            {user ? (
              <Link
                to="/account"
                className="flex items-center gap-3 rounded-2xl bg-gradient-to-l from-burgundy-500 to-burgundy-600 p-4 text-white shadow-md"
              >
                <Avatar className="h-11 w-11 ring-2 ring-white/40">
                  <AvatarFallback className="bg-white/20 text-white font-tajawal font-bold">
                    {userName.trim().charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-cairo font-bold">{userName}</p>
                  <p className="truncate text-xs text-white/75">{user.email || 'عرض حسابي وطلباتي'}</p>
                </div>
                <ChevronLeft className="h-5 w-5 shrink-0 opacity-80" />
              </Link>
            ) : (
              <div className="rounded-2xl border border-burgundy-500/20 bg-burgundy-500/5 p-4">
                <p className="font-cairo font-bold text-brand-black">أهلاً فيكِ 👋</p>
                <p className="mt-1 text-xs text-gray-500 font-cairo">سجّلي دخولك عشان تتابعي طلباتك ومفضلتك.</p>
                <Link
                  to="/login"
                  data-testid="mobile-login-btn"
                  className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-burgundy-500 py-2.5 font-cairo font-semibold text-white hover:bg-burgundy-600 transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  تسجيل الدخول
                </Link>
              </div>
            )}

            {/* الروابط */}
            <nav className="space-y-1">
              <p className="px-3 pb-1 text-xs font-cairo font-semibold text-gray-400">تصفّح</p>
              {links.map(({ to, label, icon }) => (
                <NavLink key={to} to={to} end={to === '/'} className={rowClass}>
                  {({ isActive }) => (
                    <>
                      <RowIcon icon={icon} active={isActive} />
                      <span className="flex-1">{label}</span>
                      <ChevronLeft className="h-4 w-4 text-gray-300 transition-transform group-hover:-translate-x-0.5" />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="space-y-1">
              <p className="px-3 pb-1 text-xs font-cairo font-semibold text-gray-400">حسابي</p>
              <NavLink to="/wishlist" className={rowClass}>
                {({ isActive }) => (
                  <>
                    <RowIcon icon={Heart} active={isActive} />
                    <span className="flex-1">المفضلة</span>
                    {wishlistCount > 0 && (
                      <span className="min-w-6 rounded-full bg-burgundy-500 px-2 py-0.5 text-center text-xs font-bold text-white">
                        {wishlistCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
              {user && (
                <NavLink to="/account" className={rowClass}>
                  {({ isActive }) => (
                    <>
                      <RowIcon icon={User} active={isActive} />
                      <span className="flex-1">حسابي وطلباتي</span>
                    </>
                  )}
                </NavLink>
              )}
              <button type="button" onClick={toggleTheme} className={cn(rowClass(), 'w-full text-right')} data-testid="mobile-theme-toggle">
                <RowIcon icon={theme === 'light' ? Moon : Sun} />
                <span className="flex-1">{theme === 'light' ? 'الوضع الليلي' : 'الوضع النهاري'}</span>
                <span
                  className={cn(
                    'relative h-6 w-11 rounded-full transition-colors',
                    theme === 'dark' ? 'bg-burgundy-500' : 'bg-gray-200'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all',
                      theme === 'dark' ? 'left-0.5' : 'left-[22px]'
                    )}
                  />
                </span>
              </button>
            </div>
          </div>

          {/* التذييل: تواصل + خروج */}
          <div className="border-t border-gray-200/70 dark:border-white/10 px-5 py-4 space-y-3">
            {hasContact && (
              <div className="flex items-center justify-between gap-3">
                {site.whatsapp_display ? (
                  <a href={site.tel_url} className="inline-flex items-center gap-2 font-cairo text-sm text-gray-600 dark:text-gray-300">
                    <Phone className="h-4 w-4 text-burgundy-500" />
                    <span dir="ltr">{site.whatsapp_display}</span>
                  </a>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-2">
                  {site.whatsapp_url && (
                    <a href={site.whatsapp_url} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
                       className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-green-500 hover:text-white dark:bg-white/10 transition-colors">
                      <WhatsAppIcon className="h-4 w-4" />
                    </a>
                  )}
                  {site.instagram && (
                    <a href={site.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                       className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-burgundy-500 hover:text-white dark:bg-white/10 transition-colors">
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                  {site.facebook && (
                    <a href={site.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                       className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-burgundy-500 hover:text-white dark:bg-white/10 transition-colors">
                      <Facebook className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            )}
            {user && (
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-destructive/30 py-2.5 font-cairo font-semibold text-destructive hover:bg-destructive/5 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                تسجيل الخروج
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileMenu;
